import * as Utils from "../Utils";

import {
	SurveyInfoBusinessLevel as BusinessLevel,
	GovernedChannelType,
	ICampaignDefinition,
	ICampaignDefinitionProvider,
	ICampaignDuration,
	ICampaignManager,
	ICampaignState,
	ICampaignStateProvider,
	IDictionary,
	IFloodgateEnvironmentProvider,
	IFloodgateStringProvider,
	ILogExperimentTrigger,
	ISurvey,
	ISurveyCandidacyChecker,
	ISurveyClient,
	ISurveyInfo,
	TreatmentType,
	ITriggerEventDetails,
	IEventInfo,
	ICampaignTelemetrySamplingConfig
} from "@ms-ofb/floodgate-shared-typings";
import {
	CampaignDurationSingleBuildChange,
	CampaignDurationTimeInterval,
	CampaignScopeEnvironmentCrossProduct,
	CampaignScopeUserFactAll,
	CampaignScopeUserFactAny,
} from "./CampaignDefinitionProvider";

import { CampaignState } from "./CampaignStateProvider";
import { CampaignSurveyFactory } from "./CampaignSurveyFactory";
import { IUserFact } from "@ms-ofb/officepersonalization/dist/lib/Common/Models";
import { IUserFactProvider } from "../Api/IUserFactProvider";
import { TelemetryEvent } from "../Constants";
import { getFeatureGateProvider } from "../FeatureGateProvider";
import { TelemetryLogger } from "../TelemetryLogger";

const { getDistantFuture, getDistantPast, isArray, isNOU } = Utils;

/**
 * The standard campaign manager implementation.
 */
export class CampaignManager implements ICampaignManager, ISurveyClient {
	public static isStateUpForNomination(state: ICampaignState, definition: ICampaignDefinition, date: Date, buildNumber: string): boolean {
		if (!state || !definition) {
			return false;
		}

		date = date ? date : new Date();

		// Check manual override
		if (state.ForceCandidacy) {
			return true;
		}

		// Figure out which duration we're using based on whether or not the user triggered the survey
		const campaignCooldownDuration: ICampaignDuration
			= definition.nominationScheme.getCampaignCooldown(state.IsCandidate && state.DidCandidateTriggerSurvey);

		if (!campaignCooldownDuration) {
			return false;
		}
		// First check for build-based rules. Eventually this could get more complex if we supported counting differently seen build numbers
		if (CampaignManager.hasBuildChangeDurationElapsed(campaignCooldownDuration, state.LastNominationBuildNumber, buildNumber)) {
			return true;
		}

		// Next check date rules
		return CampaignManager.hasTimeIntervalDurationElapsed(campaignCooldownDuration, state.getCooldownStartDate(), date);
	}

	public static isDateInRange(date: Date, startDate: Date, endDate: Date): boolean {
		date = date ? date : new Date();
		startDate = startDate ? startDate : getDistantPast();
		endDate = endDate ? endDate : getDistantFuture();

		// Check Start/Expire date range
		if (startDate.getTime() <= date.getTime() && endDate.getTime() >= date.getTime()) {
			return true;
		}

		return false;
	}

	public static hasTimeIntervalDurationElapsed(duration: ICampaignDuration, startDate: Date, date: Date): boolean {
		if (isNOU(duration) || !(duration instanceof CampaignDurationTimeInterval)) {
			return false;
		}
		if (!startDate || !date) {
			return false;
		}

		const endDate: Date = Utils.addSecondsWithoutOverflow(startDate, duration.intervalSeconds);
		return date.getTime() >= endDate.getTime();
	}

	public static hasBuildChangeDurationElapsed(duration: ICampaignDuration, startBuild: string, build: string): boolean {
		if (isNOU(duration) || !(duration instanceof CampaignDurationSingleBuildChange)) {
			return false;
		}
		if (isNOU(startBuild) || isNOU(build)) {
			return (startBuild === null) !== (build === null);
		}

		return !(startBuild === build);
	}

	private static isCampaignInScope(
		definition: ICampaignDefinition,
		date: Date,
		environmentProvider: IFloodgateEnvironmentProvider,
		userFactsProvider: IUserFactProvider): boolean {
		if (!definition) {
			return false;
		}

		date = date ? date : new Date();

		// Check Start/Expire date range
		if (!CampaignManager.isDateInRangeWithDefinition(date, definition)) {
			return false;
		}

		if (definition.scope) {
			if (definition.scope instanceof CampaignScopeUserFactAny || definition.scope instanceof CampaignScopeUserFactAll) {
				definition.scope.setUserFactsProvider(userFactsProvider);
			}
			if (definition.scope instanceof CampaignScopeEnvironmentCrossProduct) {
				definition.scope.setEnvironmentProvider(environmentProvider);
			}
			if (!definition.scope.isInScope()) {
				return false;
			}
		}

		// If we get here, the definition is relevant
		return true;
	}

	private static isDateInRangeWithDefinition(date: Date, definition: ICampaignDefinition): boolean {
		if (!definition) {
			return false;
		}

		date = date ? date : new Date();

		return CampaignManager.isDateInRange(date,
			// null start dates should be impossible, but if set, should mean the campaign is disabled
			definition.startTime ? definition.startTime : getDistantFuture(),
			definition.endTime);
	}

	private campaignStates: IDictionary<ICampaignState>;
	private campaignDefinitions: IDictionary<ICampaignDefinition>;
	private userFacts: IUserFact[];
	private currentBuildNumber: string;
	private stateProvider: ICampaignStateProvider;
	private definitionProviders: ICampaignDefinitionProvider[];
	private stringProvider: IFloodgateStringProvider;
	private environmentProvider: IFloodgateEnvironmentProvider;
	private surveyCandidacyChecker: ISurveyCandidacyChecker;
	private userFactsProvider: IUserFactProvider;
	private lastCampaignRefreshTime: Date;
	private isPriorityEnabled: boolean;
	private logExperimentTrigger: ILogExperimentTrigger;
	private campaignTelemetrySamplingConfig: ICampaignTelemetrySamplingConfig;
	private messagingEnabled: boolean;
	private surveyEnabled: boolean;
	private newNominationCampaigns: string[];
	private isFloodgateEnabledConfigAvailable: boolean;
	private floodgateEnabledTelemetrySamplingRate: number;

	public constructor(stateProvider: ICampaignStateProvider,
		definitionProviders: ICampaignDefinitionProvider[],
		stringProvider: IFloodgateStringProvider,
		environmentProvider: IFloodgateEnvironmentProvider,
		currentBuildNumber: string,
		currentDate: Date,
		isPriorityEnabled: boolean,
		userFactsProvider?: IUserFactProvider,
		surveyCandidacyChecker?: ISurveyCandidacyChecker,
		logExperimentTrigger?: ILogExperimentTrigger,
		campaignTelemetrySamplingConfig?: ICampaignTelemetrySamplingConfig,
		messagingEnabled?: boolean,
		surveyEnabled?: boolean) {

		if (!stateProvider) {
			throw new Error("stateProvider must not be null");
		}

		if (!isArray(definitionProviders)) {
			throw new Error("definitionProviders is either null or not an array type");
		}

		if (!stringProvider) {
			throw new Error("stringProvider must not be null");
		}

		if (isNOU(currentBuildNumber)) {
			throw new Error("currentBuildNumber must not be null");
		}

		// these settings help determine what type of messaging surface / Floodgate capability is available
		this.messagingEnabled = messagingEnabled;
		this.surveyEnabled = surveyEnabled;

		this.campaignStates = {};
		this.campaignDefinitions = {};
		this.userFacts = [];
		this.stateProvider = stateProvider;
		this.definitionProviders = definitionProviders;
		this.stringProvider = stringProvider;
		this.surveyCandidacyChecker = surveyCandidacyChecker;
		this.userFactsProvider = userFactsProvider;
		this.environmentProvider = environmentProvider; // Can be null
		this.currentBuildNumber = currentBuildNumber;

		currentDate = currentDate ? currentDate : new Date();
		this.isPriorityEnabled = isPriorityEnabled;

		this.logExperimentTrigger = logExperimentTrigger;

		this.campaignTelemetrySamplingConfig = campaignTelemetrySamplingConfig;

		this.isFloodgateEnabledConfigAvailable = getFeatureGateProvider()?.getBoolFeatureStatus("FloodgateEnabledConfigAvailable", false);
		this.floodgateEnabledTelemetrySamplingRate = getFeatureGateProvider()?.getIntFeatureStatus("FloodgateEnabledTelemetrySamplingRate", 0.1);
	}

	public LogExperimentTrigger(triggerDetails: ITriggerEventDetails[]): void {
		if (isNOU(this.logExperimentTrigger)) {
			TelemetryLogger.getTelemetryLogger().log_Error(TelemetryEvent.CampaignGovernance.LogExperimentTrigger.Undefined,
				"LogExperimentTrigger does not exist");
		} else {
			try {
				this.logExperimentTrigger.LogExperimentTrigger(triggerDetails);
			} catch (error) {
				TelemetryLogger.getTelemetryLogger().log_Error(
					TelemetryEvent.CampaignGovernance.LogExperimentTrigger.Failed, "Failed to run LogExperimentTrigger " + error);
			}
		}
	}

	// @Override
	public getActiveSurveys(): IDictionary<ISurvey> {
		const surveys: IDictionary<ISurvey> = {};
		const prioritizedCampaigns = this.getPrioritizedCampaigns();
		const activeCampaignIds = [];

		for (const key in this.campaignStates) {
			if (Object.prototype.hasOwnProperty.call(this.campaignStates, key)) {
				const state: ICampaignState = this.campaignStates[key];
				if (!state.IsCandidate) {
					continue;
				}

				// This is a requirement because we keep some otherwise "stale" campaigns around for the save routine
				const definition: ICampaignDefinition = prioritizedCampaigns[state.CampaignId];
				if (!definition) {
					continue;
				}

				const survey: ISurvey = CampaignSurveyFactory.makeSurvey(state, definition, this.stringProvider);
				if (!survey) {
					continue;
				}

				if (this.surveyCandidacyChecker && !this.surveyCandidacyChecker.isSurveyTrackable(survey)) {
					continue;
				}

				surveys[survey.getSurveyInfo().getId()] = survey;
				activeCampaignIds.push(survey.getCampaignId());
			}
		}

		this.logActiveSurveys(prioritizedCampaigns, activeCampaignIds);
		return surveys;
	}

	// This function will log a json of all the CampaignStates during creating active surveys. The purpose of this log is for us to be able to detect
	// whether the campaign is selected correctly with nomination and prioritization.
	// The sampling rate of this log is dependent on what rate was received from the campaignTelemetrySamplingConfig
	// This telemetry gets logged at every floodgate.start. So we need to keep the sampling rate low.
	// Default sampling rate: 0% - it's turned off by default. We will enable it from the Host app side if needed.
	private logActiveSurveys(prioritizedCampaigns: IDictionary<ICampaignDefinition>, activeCampaignIds: string[]) {
		const currentCampaignStates: Record<string, unknown>[] = [];
		for (const key in this.campaignStates) {
			const state: ICampaignState = this.campaignStates[key];
			const payload: Record<string, unknown> = {
				CampaignId: key,
				IsCandidate: state.IsCandidate,
				DidCandidateTriggerSurvey: state.DidCandidateTriggerSurvey,
				SurveyId: state.LastSurveyId
			}
			currentCampaignStates.push(payload);
		}

		TelemetryLogger.getTelemetryLogger().log_Event(TelemetryEvent.FloodgateEngine.GetActiveSurveys,
			{
				Message: JSON.stringify({
					CampaignStates: currentCampaignStates,
					PrioritizedCampaigns: Object.keys(prioritizedCampaigns),
					ActiveCampaigns: activeCampaignIds,
					NewNominationCampaigns: this.newNominationCampaigns
				})
			}, 0);
	}

	// @Override
	public modifyCampaignSurveyTakenDate(campaignId: string, takenDate: Date, isSurveyComplete: boolean): void {
		const state: ICampaignState = this.campaignStates[campaignId];
		if (!state) {
			return;
		}

		takenDate = takenDate ? takenDate : new Date();

		const cooldownSeconds = this.campaignDefinitions[campaignId].nominationScheme.cooldownPeriod.asTimeIntervalSeconds();
		state.markCurrentSurveyTakenOnDate(takenDate, cooldownSeconds, isSurveyComplete);

		this.saveCurrentState();
	}

	public getCampaignStates(): IDictionary<ICampaignState> {
		return this.campaignStates;
	}

	// @Override
	public getCampaignDefinitions(): IDictionary<ICampaignDefinition> {
		return this.campaignDefinitions;
	}

	public getUserFacts(): IUserFact[] {
		return this.userFacts;
	}

	// region ISurveyClient methods

	// @Override
	public onSurveyActivated(surveyInfo: ISurveyInfo, isSurveyComplete: boolean): void {
		if (!surveyInfo) {
			return;
		}

		this.modifyCampaignSurveyTakenDate(surveyInfo.getBackEndId(), new Date(), isSurveyComplete);
	}

	// @Override
	public refreshSurveyDefinitions(channelTypes?: GovernedChannelType[], date?: Date): void {
		this.refreshSurveyDefinitionsPrivate(channelTypes, (date ? date : new Date()));
	}

	// @Override
	public refreshSurveyDefinitionsAsync(channelTypes?: GovernedChannelType[], date?: Date): Promise<void> {
		return new Promise((resolve, reject) => {
			this.refreshSurveyDefinitionsPrivateAsync(channelTypes, (date ? date : new Date())).then(
				function onFulfilled() {
					resolve();
				},
			).catch(
				function OnRejected(errReason) {
					reject(errReason);
				},
			);
		});
	}

	// @Override
	public getAppSurveys(): { [id: string]: ISurvey } {
		return this.getActiveSurveys();
	}

	// @Override
	public saveCurrentState(): void {
		// Sort by campaignId, ascending for uniformity
		const states: ICampaignState[] = Utils.makeArrayFromObjectValuesSortedByKeyString(this.campaignStates);

		this.stateProvider.save(states);
	}

	// endregion

	private refreshSurveyDefinitionsPrivate(channelTypes: GovernedChannelType[], date: Date): void {
		if (this.needRefreshSurveyDefinitions()) {
			this.lastCampaignRefreshTime = new Date();
			this.campaignDefinitions = {};
			this.userFacts = [];

			this.loadFilterCampaigns(date, channelTypes);
		} else {
			this.FilterCampaignsOnRestart(date, channelTypes);
		}

		this.campaignStates = {};
		this.LoadFilterCampaignStates(date);
		this.evaluateCampaigns(date);
	}

	private refreshSurveyDefinitionsPrivateAsync(channelTypes: GovernedChannelType[], date: Date): Promise<void> {
		this.campaignStates = {};
		this.campaignDefinitions = {};
		this.userFacts = [];
		this.lastCampaignRefreshTime = this.lastCampaignRefreshTime ?? new Date();

		return new Promise((resolve, reject) => {
			this.loadAndFilterCampaignDataAsync(date, channelTypes).then(
				() => {
					this.evaluateCampaigns(date);
					resolve();
				},
			).catch(
				function OnRejected(errReason) {
					reject(errReason);
				},
			);
		});
	}

	/**
	 * Load and filter the campaigns definitions and states
	 */
	private loadAndFilterCampaignDataAsync(currentDate: Date, channelTypes: GovernedChannelType[]): Promise<void> {
		currentDate = currentDate ? currentDate : new Date();
		let loadedDefinitions: ICampaignDefinition[] = [];
		const promises = [];

		const userFactsPromise = this.userFactsProvider && this.userFactsProvider.loadAsync() as Promise<IUserFact[]>;

		for (const i of Object.keys(this.definitionProviders)) {
			const provider = this.definitionProviders[i];
			let promise: any = provider && provider.loadAsync();
			if (!isNOU(promise)) {
				// Promise.all fails even if one promise fails.
				// We want to ignore the promise that failed and continue with the ones
				// those are succesful, hence overriding the catch to always resolve.
				// responsibility of logging failures should be with the provider.
				promise = promise.catch(() => {
					TelemetryLogger.getTelemetryLogger().log_CampaignLoad_Failed("Failed to load from campaign definition provider");
					Promise.resolve();
				});

				promises.push(promise);
			}
		}

		return new Promise((resolve, reject) => {
			Promise.all(promises).then((values) => {
				for (const i of Object.keys(values)) {
					const campaignDefinitions = values[i];
					if (isArray(campaignDefinitions)) {
						// If there are campaign defintions with duplicate campaign Id's
						// Floodgate will load the last one it found. FilterCampaignData does this filteration
						loadedDefinitions = loadedDefinitions.concat(campaignDefinitions);
					}
				}

				this.logInitialLoadCampaigns(loadedDefinitions, "loadAsync", this.campaignTelemetrySamplingConfig?.campaignInitialLoadSamplingRate);

				const setUserFacts = (userFacts?: IUserFact[]) => {
					this.userFacts = userFacts || [];
					this.FilterCampaignData(currentDate, channelTypes, loadedDefinitions);
					resolve();
				};

				// Before filtering and evaluating the campaign definitions,
				// wait until the user facts are loaded as they will be used in evaluating campaign scopes
				if (!isNOU(userFactsPromise)) {
					userFactsPromise.then((userFacts: IUserFact[]) => {
						setUserFacts(userFacts);
					}).catch(() => {
						// this should never happen as user facts promise will always be resolved by the user facts provider
						TelemetryLogger.getTelemetryLogger()
							.log_Error(TelemetryEvent.Floodgate.UserFactsLoad.Failed, "Failed to load from user facts provider");
						setUserFacts();
					});
				} else {
					setUserFacts();
				}
			},
			).catch(
				function OnRejected(error) {
					// this should never happen as Promise.all will always be resolved
					// because of hack at the top of the function.
					TelemetryLogger.getTelemetryLogger().log_CampaignLoad_Failed("Failed to load from campaign definition provider");
				},
			);
		});
	}

	// This function will log a json of all the definitions received on initial load. The purpose of this log is for us to be able to detect
	// what campaigns users initially recieved and trace which campaigns from the original pool were successfully nominated / launched
	// The sampling rate of this log is dependent on what rate was received from the campaignTelemetrySamplingConfig
	// This telemetry gets logged once per floodgate session
	// Default sampling rate: 10%
	private logInitialLoadCampaigns(loadedDefinitions: ICampaignDefinition[], loadMethod: string, initialLoadSamplingRate = 0.1) {
		if (initialLoadSamplingRate > 0) {
			const loggedDefinitions: Record<string, unknown>[] = [];
			for (const key in loadedDefinitions) {
				const definition = loadedDefinitions[key];
				const payload: Record<string, unknown> = {
					id: definition.campaignId,
					businessLevel: definition.businessLevel,
					treatmentType: definition.treatmentType,
					scope: definition.scope,
					telemetryEventName: definition?.surveyTemplate?.metadata?.contentMetadata?.["telemetryEventName"],
					userHost: this.environmentProvider,
					governedChannelType: definition.governedChannelType,
					loadMethod: loadMethod,
				}
				loggedDefinitions.push(payload);
			}
			TelemetryLogger.getTelemetryLogger().log_Event(TelemetryEvent.FloodgateEngine.InitialLoadedDefinitions,
				{ Message: JSON.stringify(loggedDefinitions) },
				initialLoadSamplingRate)
		}
	}

	private isCampaignTypeEnabled(governedChannelType: GovernedChannelType): boolean {

		// default to true unless stated otherwise by the associated enabled settings
		let isCampaignTypeEnabled = true;

		// feature gate for Floodgate Enabled feature- if this is on we will use the settings to determine whether to filter at this stage
		if (this.isFloodgateEnabledConfigAvailable) {
			// LPP campaigns depend on the messagingEnabled check (currently teaching message or busbar)
			if (governedChannelType == GovernedChannelType.TeachingMessage || governedChannelType == GovernedChannelType.BusinessBar) {
				isCampaignTypeEnabled = this.messagingEnabled;
			} else {
				// Quip surveys will depend on the surveyEnabled check
				isCampaignTypeEnabled = this.surveyEnabled;
			}
		}

		// we return default true if we did not receive floodgate enabled config or the feature gate is not on
		return isCampaignTypeEnabled;
	}


	private loadFilterCampaigns(currentDate: Date, channelTypes: GovernedChannelType[]): void {
		currentDate = currentDate ? currentDate : new Date();
		let loadedDefinitions: ICampaignDefinition[] = [];

		for (const index of Object.keys(this.definitionProviders)) {
			try {
				const definitions = this.definitionProviders[index] && this.definitionProviders[index].load();
				if (isArray(definitions)) {
					// If there are campaign defintions with duplicate campaign Id's
					// Floodgate will load the last one it found. FilterCampaignData does this filteration
					loadedDefinitions = loadedDefinitions.concat(definitions);
				}
			} catch (error) {
				// Log error, but continue with other providers
				// It should be the responsibility of provider to log detailed errors
				TelemetryLogger.getTelemetryLogger().log_CampaignLoad_Failed("Failed to load from campaign definition provider. " + error.toString());
			}
		}

		this.logInitialLoadCampaigns(loadedDefinitions, "load", this.campaignTelemetrySamplingConfig?.campaignInitialLoadSamplingRate);

		// Load the user facts before all the campaign definitions are evaluated
		try {
			this.userFacts = (this.userFactsProvider && this.userFactsProvider.load()) || [];
		} catch (error) {
			TelemetryLogger.getTelemetryLogger().log_Error(
				TelemetryEvent.Floodgate.UserFactsLoad.Failed, "Failed to load from user facts provider. " + error);
		}

		// array that lists the skipped campaigns due to the campaign type not being enabled
		const disabledCampaigns = new Array<string>()

		// array that will be passed to LPP scorecard callback
		const triggerDetails = new Array<ITriggerEventDetails>();

		// array that lists campaign ids which passed governed channel type / business level checks
		const channelCleared = new Array<string>()

		// array that lists campaign ids that are in scope 
		const inScope = new Array<string>();

		// array that lists campaign ids that are not control campaigns (added to activityListener)
		const nominated = new Array<string>();

		// Load filtered campaign definitions
		for (const key in loadedDefinitions) {
			if (Object.prototype.hasOwnProperty.call(loadedDefinitions, key)) {
				const definition: ICampaignDefinition = loadedDefinitions[key];

				// checks if this definition should be included in nomination process based on Floodgate Enabled configuration
				if (!this.isCampaignTypeEnabled(definition.governedChannelType)) {
					disabledCampaigns.push(definition.campaignId)
					continue;
				}

				// this needs to be done here before adding to campaignDefinitions in case there are multiple definitions with different language scopes
				if (!CampaignManager.isCampaignInScope(definition, currentDate, this.environmentProvider, this.userFactsProvider)) {
					continue;
				}
				inScope.push(definition.campaignId);

				// filtering for campaign trigger logging
				if (definition.treatmentType !== TreatmentType.none) {
					// campaign trigger info for scorecards
					const triggerDetail = this.RetrieveTriggerEventDetail(definition);
					if (!isNOU(triggerDetail)) {
						triggerDetails.push(triggerDetail);
					}
				}

				if (channelTypes &&
					(channelTypes.indexOf(definition.governedChannelType) < 0) &&
					(definition.businessLevel !== BusinessLevel.Critical)) {
					continue;
				}
				channelCleared.push(definition.campaignId);

				// to remove control campaigns from activityListener list 
				if (definition.treatmentType === TreatmentType.Control) {
					continue;
				}

				// log the definitions which are nominated
				nominated.push(definition.campaignId);
				// Overwrite the campaign definition if it already exists with the latest one.
				this.campaignDefinitions[definition.campaignId] = definition;
			}
		}

		// log the campaigns which were skipped due to the campaign type being disabled
		this.CampaignTypeDisabledTelemetry(disabledCampaigns, this.messagingEnabled, this.surveyEnabled);

		// if sampling is on for governance check telemetry, log telemetry
		this.GovernanceCheckTelemetry(channelCleared, inScope, nominated, this.campaignTelemetrySamplingConfig?.passedCampaignGovernanceSamplingRate);

		// call new scorecard trigger callback only if feature gate is on and there are campaign definitions available
		if (triggerDetails.length > 0) {
			this.LogExperimentTrigger(triggerDetails);
		}
	}

	private RetrieveTriggerEventDetail(definition: ICampaignDefinition): ITriggerEventDetails {
		try {
			const eventInfo: IEventInfo = {
				id: definition.campaignId,
				telemetryEventName: definition?.surveyTemplate?.metadata?.contentMetadata?.["telemetryEventName"],
				treatmentType: isNOU(definition.treatmentType) ? TreatmentType.none : definition.treatmentType
			}

			const triggerDetail: ITriggerEventDetails = {
				triggerEvent: eventInfo.telemetryEventName,
				eventInfo: eventInfo
			}

			// return info needed for scorecards for this campaign to the list to the array
			return triggerDetail;
		} catch (error) {
			TelemetryLogger.getTelemetryLogger().log_Error(
				TelemetryEvent.CampaignGovernance.RetrieveTriggerEventDetail.Failed, "Failed to add trigger event detail " + error);
			return null;
		}
	}

	// creating a json object to log campaigns which passed various governance checks, logs once per floodgate session
	// Default sampling rate: 10%
	private GovernanceCheckTelemetry(channelCleared: string[], inScope: string[], nominated: string[], samplingRate = 0.1)
		: void {
		if (samplingRate > 0) {
			const payload: Record<string, unknown> = {
				channelCleared: channelCleared.length > 0 ? channelCleared : undefined,
				inScope: inScope.length > 0 ? inScope : undefined,
				nominated: nominated.length > 0 ? nominated : undefined,
			}
			if (Object.values(payload).some(v => v != undefined)) {
				TelemetryLogger.getTelemetryLogger().log_Event(TelemetryEvent.CampaignGovernance.NominationCheckCampaigns,
					{ Message: JSON.stringify(payload) }, samplingRate);
			}
		}
	}

	private CampaignTypeDisabledTelemetry(disabledCampaigns, messagingEnabled = true, surveyEnabled = true) {
		if (this.isFloodgateEnabledConfigAvailable && disabledCampaigns.length > 0) {
			const payload: Record<string, unknown> = {
				disabledCampaigns: disabledCampaigns,
				messagingEnabled: messagingEnabled,
				surveyEnabled: surveyEnabled,
			}

			// default we will log the telemetry at 10%, we can change this later on if needed
			TelemetryLogger.getTelemetryLogger().log_Event(TelemetryEvent.FloodgateEngine.CampaignTypeDisabledDefinitions,
				{ Message: JSON.stringify(payload) }, this.floodgateEnabledTelemetrySamplingRate);
		}
	}

	/**
	 * Load and filter the campaigns definitions and states
	 */
	private FilterCampaignData(currentDate: Date, channelTypes: GovernedChannelType[], loadedDefinitions: ICampaignDefinition[]): void {
		const loadedDefinitionsMap: IDictionary<ICampaignDefinition> = {};
		const triggerDetails = new Array<ITriggerEventDetails>();

		// array that lists the skipped campaigns due to the campaign type not being enabled
		const campaignTypeDisabledCampaigns = new Array<string>()

		// array that lists campaign ids which passed governed channel type / business level checks
		const channelCleared = new Array<string>()

		// array that lists campaign ids that are in scope 
		const inScope = new Array<string>();

		// array that lists campaign ids that are not control campaigns (added to activityListener)
		const nominated = new Array<string>();
		// Load filtered campaign definitions
		for (const key in loadedDefinitions) {
			if (Object.prototype.hasOwnProperty.call(loadedDefinitions, key)) {
				const definition: ICampaignDefinition = loadedDefinitions[key];

				loadedDefinitionsMap[definition.campaignId] = definition;

				// checks if this definition should be included in nomination process based on Floodgate Enabled configuration
				if (!this.isCampaignTypeEnabled(definition.governedChannelType)) {
					campaignTypeDisabledCampaigns.push(definition.campaignId)
					continue;
				}

				// this needs to be done here before adding to campaignDefinitions in case there are multiple definitions with different language scopes
				if (!CampaignManager.isCampaignInScope(definition, currentDate, this.environmentProvider, this.userFactsProvider)) {
					continue;
				}
				inScope.push(definition.campaignId);

				// filtering for campaign trigger logging
				if (definition.treatmentType !== TreatmentType.none) {
					// campaign trigger info for scorecards
					const triggerDetail = this.RetrieveTriggerEventDetail(definition);
					if (!isNOU(triggerDetail)) {
						triggerDetails.push(triggerDetail);
					}
				}

				if (channelTypes &&
					(channelTypes.indexOf(definition.governedChannelType) < 0) &&
					(definition.businessLevel !== BusinessLevel.Critical)) {
					continue;
				}
				channelCleared.push(definition.campaignId);

				// to remove control campaigns from activityListener list 
				if (definition.treatmentType === TreatmentType.Control) {
					continue;
				}

				// log the definitions which are nominated
				nominated.push(definition.campaignId);
				// Overwrite the campaign definition if it already exists with the latest one.
				this.campaignDefinitions[definition.campaignId] = definition;
			}
		}

		// log the campaigns which were skipped due to the campaign type being disabled
		this.CampaignTypeDisabledTelemetry(campaignTypeDisabledCampaigns, this.messagingEnabled, this.surveyEnabled);

		// if sampling is on for governance check telemetry, log telemetry
		this.GovernanceCheckTelemetry(channelCleared, inScope, nominated, this.campaignTelemetrySamplingConfig?.passedCampaignGovernanceSamplingRate);

		// call new scorecard trigger callback only if feature gate is on and there are campaign definitions available
		if (triggerDetails.length > 0) {
			this.LogExperimentTrigger(triggerDetails);
		}

		// Load campaign state, filtering out the expired definitions
		const loadedStates: ICampaignState[] = this.stateProvider.load();
		const staleLoadedStates: ICampaignState[] = [];

		for (const key in loadedStates) {
			if (Object.prototype.hasOwnProperty.call(loadedStates, key)) {
				const state: ICampaignState = loadedStates[key];

				if (!Object.prototype.hasOwnProperty.call(this.campaignDefinitions, state.CampaignId)) {
					staleLoadedStates.push(state);
				}

				this.campaignStates[state.CampaignId] = state;
			}
		}

		/*
			* Stale State data cleanup
			* State needs to be cleaned up when:
			* 1) The campaign has expired
			* 2) The campaign is no longer present in the definitions (think ECS or other outages or very old expirations)
			*    In this case the state should still be cleaned up on it's reelection date
			* 3) When the user is out of scope (for an otherwise active campaign) and is up for nomination

			* This optimizes the user experience in case the campaign pops up or gets renewed when we would have preferred the user remain in cool down

			* 2&3 are basically the same, and 1 is a special case optimization on top of 2&3 for earlier clean up

			* To do 2 & 3, we just look at states with no matching definition (after filtering), and reject them if they are past their nomination period
			* To do 1, we just need to keep a list/hash of the definitions that we filtered out this load cycle, specifically for expiration dates, and bypass
			* the cool down "wait" for these specifically

			* For now, allowing some "grace" for "missing"/"blippy" campaign definitions (in case they pop in and out of ECS) by deferring missing
			* deletion by a delay-time specified by the original campaign.
			*/
		for (const key in staleLoadedStates) {
			if (Object.prototype.hasOwnProperty.call(staleLoadedStates, key)) {
				const state: ICampaignState = staleLoadedStates[key];
				if (!state) {
					continue;
				}

				const definition: ICampaignDefinition = loadedDefinitionsMap[state.CampaignId];
				let shouldRemove = false;

				if (!definition) {
					if (state.LastNominationTimeUtc
						.getTime() <= Utils.subtractSecondsWithoutOverflow(currentDate, state.DeleteAfterSecondsWhenStale).getTime()) {
						shouldRemove = true;
					}
				} else if (CampaignManager.isStateUpForNomination(state, definition, currentDate, this.currentBuildNumber)) {
					shouldRemove = true;
				}

				// The next time we write to storage, these will be removed
				if (shouldRemove) {
					delete this.campaignStates[state.CampaignId];
				}
			}
		}
	}

	// Filter lower priority campaigns that are on same channel and have a matching tag
	private getPrioritizedCampaigns() {
		const prioritizedCampaigns: IDictionary<ICampaignDefinition> = {};

		// build <tagId_channel, campaign[]> map
		const matchingTagCampaigns: IDictionary<ICampaignDefinition[]> = {};
		Object.keys(this.campaignDefinitions || []).forEach((key) => {
			const definition: ICampaignDefinition = this.campaignDefinitions[key];
			if (Object.prototype.hasOwnProperty.call(this.campaignStates, key)) {
				const state: ICampaignState = this.campaignStates[key];
				// only add the campaign if it's nominated
				if (state.IsCandidate) {
					prioritizedCampaigns[key] = definition;

					// if tags are not defined, use empty tag to fallback to channel level aggregation
					const definitionTags = definition.tags && definition.tags.length > 0 ? definition.tags : [""];
					definitionTags.forEach((tagId) => {
						const mapKey = `${tagId}_${definition.governedChannelType}`;
						matchingTagCampaigns[mapKey] = matchingTagCampaigns[mapKey] || [];
						matchingTagCampaigns[mapKey].push(definition);
					});
				}
			}
		});

		// remove lower priority campaigns
		Object.keys(matchingTagCampaigns).forEach((key) => {
			// Check if the priority filter is enabled for standard channel
			if (!this.isPriorityEnabled && key.endsWith(`_${GovernedChannelType.Standard}`)) {
				return;
			}

			const definitions = matchingTagCampaigns[key];
			if (definitions.length <= 1) {
				return;
			}

			// Get definition that has max priority value (0/null/undefined has highest prioirty)
			const [maxPriorityDefinition, hasLowerPriorityDefinitions] = Utils.findHighestPriorityDefinition(definitions);

			const maxPriority = maxPriorityDefinition.primaryPriority ?? 0;
			if (!hasLowerPriorityDefinitions) {
				// nothing to filter because all priorities are the same
				return;
			}

			// delete lower priority campaigns while preserving all the matching higher priority items
			definitions.forEach((current) => {
				const currentPriority = current.primaryPriority;

				// for mso compatability ignore campaigns without priority set so that they are always considered
				if (!isNOU(current.primaryPriority) && currentPriority > maxPriority) {
					delete prioritizedCampaigns[current.campaignId];
				}
			});
		});

		return prioritizedCampaigns;
	}

	/**
	 * Given the loaded campaign definitions and states from previous sessions, run anything up for nomination
	 */
	private evaluateCampaigns(currentDate: Date): void {
		// Loop through campaigns definitions, and update their state if necessary
		currentDate = currentDate ? currentDate : new Date();
		this.newNominationCampaigns = [];

		for (const key in this.campaignDefinitions) {
			if (Object.prototype.hasOwnProperty.call(this.campaignDefinitions, key)) {
				const definition: ICampaignDefinition = this.campaignDefinitions[key];
				const state: ICampaignState = this.campaignStates[definition.campaignId];

				if (!state || CampaignManager.isStateUpForNomination(state, definition, currentDate, this.currentBuildNumber)) {
					let lastSurveyId: string = state ? state.LastSurveyId : "";
					let lastSurveyStartTime: Date = state ? state.LastSurveyStartTimeUtc : getDistantPast();
					let lastSurveyExpirationTime: Date = state ? state.LastSurveyExpirationTimeUtc : getDistantPast();
					const lastSurveyActivatedTime: Date = state ? state.LastSurveyActivatedTimeUtc : getDistantPast();
					const lastCooldownEndTimeUtc: Date = state ? state.LastCooldownEndTimeUtc : getDistantPast();
					this.newNominationCampaigns.push(definition.campaignId);

					// Run the nomination
					const isCandidate: boolean = (state && state.ForceCandidacy) || definition.nominationScheme.evaluateNominationRules();
					if (isCandidate) {
						// Make new survey properties for the next call to get the active surveys
						lastSurveyId = Utils.guid();
						lastSurveyStartTime = definition.nominationScheme.calculateSurveyStartTimeFromDate(currentDate);
						lastSurveyExpirationTime = definition.nominationScheme.calculateSurveyExpirationTimeFromSurveyStartTime(lastSurveyStartTime);
					}

					const newState: ICampaignState = new CampaignState(
						definition.campaignId,
						currentDate,
						this.currentBuildNumber,
						definition.nominationScheme.getActiveSurveyTimeIntervalSeconds(),
						false,
						isCandidate,
						false,
						lastSurveyActivatedTime,
						lastSurveyId,
						lastSurveyStartTime,
						lastSurveyExpirationTime,
						lastCooldownEndTimeUtc,
					);

					// Save the new state to our local cache
					this.campaignStates[newState.CampaignId] = newState;
				}
			}
		}
	}

	private needRefreshSurveyDefinitions(): boolean {
		// Check for data has changed
		let updateNeeded = false;

		for (const i of Object.keys(this.definitionProviders)) {
			const provider = this.definitionProviders[i];
			updateNeeded = provider.hasDataChanged();

			// Breakout singe we just need one true to refresh campaigns
			if (updateNeeded) {
				break;
			}
		}

		return !this.lastCampaignRefreshTime || updateNeeded;
	}

	private FilterCampaignsOnRestart(currentDate: Date, channelTypes: GovernedChannelType[]): void {
		for (const key in this.campaignDefinitions) {
			if (Object.prototype.hasOwnProperty.call(this.campaignDefinitions, key)) {
				const definition: ICampaignDefinition = this.campaignDefinitions[key];

				// checks if this definition should be included in nomination process based on Floodgate Enabled configuration
				if (!this.isCampaignTypeEnabled(definition.governedChannelType)) {
					delete this.campaignDefinitions[definition.campaignId];
					continue;
				}

				if (channelTypes &&
					(channelTypes.indexOf(definition.governedChannelType) < 0) &&
					(definition.businessLevel !== BusinessLevel.Critical)) {
					delete this.campaignDefinitions[definition.campaignId];
					continue;
				}

				if (!CampaignManager.isCampaignInScope(definition, currentDate, this.environmentProvider, this.userFactsProvider)) {
					delete this.campaignDefinitions[definition.campaignId];
					continue;
				}

				// new campaign governance only applies if feature gate is on, removes control campaigns from activityListener list 
				if (definition.treatmentType === TreatmentType.Control) {
					delete this.campaignDefinitions[definition.campaignId];
					continue;
				}
			}
		}
	}

	/**
	 * Load and filter the campaigns definitions and states
	 */
	private LoadFilterCampaignStates(currentDate: Date): void {
		// Load campaign state, filtering out the expired definitions
		const loadedStates: ICampaignState[] = this.stateProvider.load();
		const staleLoadedStates: ICampaignState[] = [];

		for (const key in loadedStates) {
			if (Object.prototype.hasOwnProperty.call(loadedStates, key)) {
				const state: ICampaignState = loadedStates[key];

				if (!Object.prototype.hasOwnProperty.call(this.campaignDefinitions, state.CampaignId)) {
					staleLoadedStates.push(state);
				}

				this.campaignStates[state.CampaignId] = state;
			}
		}

		/*
		 * Stale State data cleanup
		 * State needs to be cleaned up when:
		 * 1) The campaign has expired
		 * 2) The campaign is no longer present in the definitions (think ECS or other outages or very old expirations)
		 *    In this case the state should still be cleaned up on it's reelection date
		 * 3) When the user is out of scope (for an otherwise active campaign) and is up for nomination

		 * This optimizes the user experience in case the campaign pops up or gets renewed when we would have preferred the user remain in cool down

		 * 2&3 are basically the same, and 1 is a special case optimization on top of 2&3 for earlier clean up

		 * To do 2 & 3, we just look at states with no matching definition (after filtering), and reject them if they are past their nomination period
		 * To do 1, we just need to keep a list/hash of the definitions that we filtered out this load cycle, specifically for expiration dates, and bypass
		 * the cool down "wait" for these specifically

		 * For now, allowing some "grace" for "missing"/"blippy" campaign definitions (in case they pop in and out of ECS) by deferring missing
		 * deletion by a delay-time specified by the original campaign.
		 */
		for (const key in staleLoadedStates) {
			if (Object.prototype.hasOwnProperty.call(staleLoadedStates, key)) {
				const state: ICampaignState = staleLoadedStates[key];
				if (!state) {
					continue;
				}

				const definition: ICampaignDefinition = this.campaignDefinitions[state.CampaignId];
				let shouldRemove = false;

				if (!definition) {
					if (state.LastNominationTimeUtc
						.getTime() <= Utils.subtractSecondsWithoutOverflow(currentDate, state.DeleteAfterSecondsWhenStale).getTime()) {
						shouldRemove = true;
					}
				} else if (CampaignManager.isStateUpForNomination(state, definition, currentDate, this.currentBuildNumber)) {
					shouldRemove = true;
				}

				// The next time we write to storage, these will be removed
				if (shouldRemove) {
					delete this.campaignStates[state.CampaignId];
				}
			}
		}
	}
}
